// DEMO COMPONENT - REMOVE IN PRODUCTION
// This component is for demo purposes only to test different portrait ratios
// To disable: Comment out the import and usage in the main component
// To enable: Uncomment the import and usage

import React, { useState } from 'react';
import { Monitor, Smartphone, Tablet } from 'lucide-react';
import { useDemoRatio } from '@/contexts/DemoContext';
import { typeRole } from '@/styles/typography';

export type PortraitRatio = '16:9' | '18.5:9' | 'freestyle';

interface DemoRatioToggleProps {
    onRatioChange?: (ratio: PortraitRatio) => void;
}

export default function DemoRatioToggle({ onRatioChange }: DemoRatioToggleProps) {
    const [isOpen, setIsOpen] = useState(false);
    const { portraitRatio, setPortraitRatio } = useDemoRatio();

    const ratios = [
        {
            value: '16:9' as PortraitRatio,
            label: 'Normal 16:9',
            icon: Monitor,
            description: 'Standard portrait ratio'
        },
        {
            value: '18.5:9' as PortraitRatio,
            label: 'iPhone 18.5:9',
            icon: Smartphone,
            description: 'Modern smartphone ratio'
        },
        {
            value: 'freestyle' as PortraitRatio,
            label: 'Free Style (Web)',
            icon: Tablet,
            description: 'Full responsive web'
        }
    ];

    const handleRatioSelect = (ratio: PortraitRatio) => {
        setPortraitRatio(ratio);
        setIsOpen(false);
        onRatioChange?.(ratio);
    };

    const currentRatio = ratios.find(r => r.value === portraitRatio);
    const CurrentIcon = currentRatio?.icon || Monitor;

    return (
        <div className="fixed top-4 right-4 z-50">
            <div className="relative">
                {/* Toggle Button */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`flex items-center gap-2 px-3 py-2 bg-black/80 backdrop-blur-md text-white ${typeRole.caption} font-medium rounded-lg border border-border-ice hover:bg-black/90 transition-all duration-200 shadow-lg`}
                >
                    <CurrentIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">{currentRatio?.label}</span>
                    <span className="sm:hidden">{portraitRatio}</span>
                    <svg
                        className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {/* Dropdown Menu */}
                {isOpen && (
                    <div className="absolute top-full right-0 mt-2 w-48 bg-black/90 backdrop-blur-md border border-border-ice rounded-lg shadow-xl overflow-hidden">
                        {ratios.map((ratio) => {
                            const IconComponent = ratio.icon;
                            return (
                                <button
                                    key={ratio.value}
                                    onClick={() => handleRatioSelect(ratio.value)}
                                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/10 transition-colors duration-200 ${portraitRatio === ratio.value ? 'bg-white/10' : ''
                                        }`}
                                >
                                    <IconComponent className="w-4 h-4 text-white flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <div className={`text-white ${typeRole.body} font-medium`}>
                                            {ratio.label}
                                        </div>
                                        <div className={`text-caption-muted ${typeRole.caption}`}>
                                            {ratio.description}
                                        </div>
                                    </div>
                                    {portraitRatio === ratio.value && (
                                        <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Click outside to close */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-[-1]"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
}

// DEMO UTILITY FUNCTIONS
export const getRatioStyles = (ratio: PortraitRatio) => {
    switch (ratio) {
        case '16:9':
            return {
                maxWidth: '28rem', // 448px - typical 16:9 portrait width
                aspectRatio: '9/16',
                containerClass: 'demo-ratio-16-9'
            };
        case '18.5:9':
            return {
                maxWidth: '26rem', // 416px - typical 18.5:9 portrait width  
                aspectRatio: '9/18.5',
                containerClass: 'demo-ratio-18-5-9'
            };
        case 'freestyle':
        default:
            return {
                maxWidth: 'none',
                aspectRatio: 'auto',
                containerClass: 'demo-ratio-freestyle'
            };
    }
};

export const applyRatioStyles = (ratio: PortraitRatio) => {
    const styles = getRatioStyles(ratio);

    if (ratio === 'freestyle') {
        return {
            maxWidth: 'none',
            width: '100%'
        };
    }

    return {
        maxWidth: styles.maxWidth,
        aspectRatio: styles.aspectRatio,
        width: '100%',
        margin: '0 auto'
    };
};
